import styles from "./cta-button.module.css";
import { filterClasses } from "BEDROCK/utility/filter-classes";
import { UIIcon } from "BEDROCK/components/ui-icon-drawing/ui-icon";
import { UIIconName } from "BEDROCK/components/ui-icon-drawing/ui-icon-type";

type Variant = "PRIMARY" | "SECONDARY" | "LINK" | "LINK-SECONDARY";
type IconPosition = "LEFT" | "RIGHT";

interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  title?: string;
  iconName?: UIIconName;
  iconPosition?: IconPosition;
  iconSize?: number;
  children?: React.ReactNode;
  disabled?: boolean;
  destructive?: boolean;
}

export const CTAButton = ({
  variant = "PRIMARY",
  title,
  children,
  iconName,
  iconPosition,
  iconSize,
  className,
  disabled,
  destructive,
  ...rest
}: CTAButtonProps) => {
  if (variant === "SECONDARY") {
    return (
      <SecondaryCTAButton
        title={title}
        iconName={iconName}
        iconPosition={iconPosition}
        className={filterClasses(className, disabled ? styles.disabled : null)}
        destructive={destructive}
        {...rest}
      >
        {children}
      </SecondaryCTAButton>
    );
  }
  if (variant === "LINK") {
    return (
      <LinkCTAButton
        title={title}
        iconName={iconName}
        iconPosition={iconPosition}
        className={filterClasses(className, disabled ? styles.disabled : null)}
        destructive={destructive}
        {...rest}
      >
        {children}
      </LinkCTAButton>
    );
  }
  if (variant === "LINK-SECONDARY") {
    return (
      <LinkSecondaryCTAButton
        title={title}
        iconName={iconName}
        iconPosition={iconPosition}
        className={filterClasses(className, disabled ? styles.disabled : null)}
        destructive={destructive}
        {...rest}
      >
        {children}
      </LinkSecondaryCTAButton>
    );
  }

  return (
    <button
      className={filterClasses(
        styles.button,
        destructive ? styles.ctaPrimaryDestructive : styles.ctaPrimary,
        className,
        disabled ? styles.disabled : null,
        iconName
          ? iconPosition === undefined || iconPosition === "LEFT"
            ? styles.left
            : styles.right
          : styles.centered
      )}
      {...rest}
    >
      {iconName && (iconPosition === undefined || iconPosition === "LEFT") && (
        <UIIcon iconName={iconName} variant="CONTRAST" size={iconSize ?? 25} />
      )}
      {title}
      {children}
      {iconName && iconPosition === "RIGHT" && (
        <UIIcon iconName={iconName} variant="CONTRAST" size={25} />
      )}
    </button>
  );
};

interface SecondaryCTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title?: string;
  iconName?: UIIconName;
  iconPosition?: IconPosition;
  iconSize?: number;
  children?: React.ReactNode;
  destructive?: boolean;
}

export const SecondaryCTAButton = ({
  title,
  iconName,
  iconPosition,
  iconSize,
  children,
  className,
  destructive,
  ...rest
}: SecondaryCTAButtonProps) => {
  return (
    <button
      className={filterClasses(
        styles.button,
        destructive ? styles.ctaSecondaryDestructive : styles.ctaSecondary,
        className,
        iconName
          ? iconPosition === undefined || iconPosition === "LEFT"
            ? styles.left
            : styles.right
          : styles.centered,
        destructive ? styles.destructive : null
      )}
      {...rest}
    >
      {iconName && (iconPosition === undefined || iconPosition === "LEFT") && (
        <UIIcon iconName={iconName} variant="SELECTED" size={iconSize ?? 25} />
      )}
      {title}
      {children}
      {iconName && iconPosition === "RIGHT" && (
        <UIIcon iconName={iconName} variant="SELECTED" size={iconSize ?? 25} />
      )}
    </button>
  );
};

interface LinkCTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title?: string;
  iconName?: UIIconName;
  iconPosition?: IconPosition;
  iconSize?: number;
  children?: React.ReactNode;
  destructive?: boolean;
}

export const LinkCTAButton = ({
  title,
  iconName,
  iconPosition,
  iconSize,
  children,
  className,
  destructive,
  ...rest
}: LinkCTAButtonProps) => {
  return (
    <button
      className={filterClasses(
        styles.button,
        destructive ? styles.ctaLinkDestructive : styles.ctaLink,
        className,
        iconName
          ? iconPosition === undefined || iconPosition === "LEFT"
            ? styles.left
            : styles.right
          : styles.centered
      )}
      {...rest}
    >
      {iconName && (iconPosition === undefined || iconPosition === "LEFT") && (
        <UIIcon iconName={iconName} variant="SELECTED" size={iconSize ?? 22} />
      )}
      {title}
      {children}
      {iconName && iconPosition === "RIGHT" && (
        <UIIcon iconName={iconName} variant="SELECTED" size={iconSize ?? 22} />
      )}
    </button>
  );
};

interface LinkSecondaryCTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title?: string;
  iconName?: UIIconName;
  iconPosition?: IconPosition;
  iconSize?: number;
  children?: React.ReactNode;
  destructive?: boolean;
}

export const LinkSecondaryCTAButton = ({
  title,
  iconName,
  iconPosition,
  iconSize,
  children,
  className,
  destructive,
  ...rest
}: LinkSecondaryCTAButtonProps) => {
  return (
    <button
      className={filterClasses(
        styles.button,
        destructive ? styles.ctaLinkDestructive : styles.ctaLink,
        styles.ctaLinkSecondary,
        className,
        iconName
          ? iconPosition === undefined || iconPosition === "LEFT"
            ? styles.left
            : styles.right
          : styles.centered
      )}
      {...rest}
    >
      {iconName && (iconPosition === undefined || iconPosition === "LEFT") && (
        <UIIcon iconName={iconName} variant="SELECTED" size={iconSize ?? 22} />
      )}
      {title}
      {children}
      {iconName && iconPosition === "RIGHT" && (
        <UIIcon iconName={iconName} variant="SELECTED" size={iconSize ?? 22} />
      )}
    </button>
  );
};
